// Operator › Billing (`/operator/billing?tab=&month=&company=`; WALLET-P1 §6.2, BILLING-PLANS
// §6.2): agency users only; anyone else gets the not-found page and no API call, as the API
// answers them 404. `company`: Subscriptions opens with that company expanded (an inbox item).
import { createRoute, useNavigate } from "@tanstack/react-router";
import { utcMonth } from "../features/billing/billing-queries";
import { isOperator } from "../features/company";
import {
  BILLING_TABS,
  type BillingTab,
  OperatorBillingPage,
} from "../features/operator/billing/operator-billing-page";
import { useSession } from "../features/use-feature";
import { NotFoundPage } from "../pages/status-pages";
import { OperatorShell, PlainShell } from "../shell/app-shell";
import { authedRoute } from "./_authed";

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;

export const operatorBillingRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: "operator/billing",
  validateSearch: (
    search: Record<string, unknown>,
  ): { tab?: BillingTab; month?: string; company?: string } => ({
    ...(BILLING_TABS.includes(search.tab as BillingTab) ? { tab: search.tab as BillingTab } : {}),
    ...(typeof search.month === "string" && MONTH.test(search.month)
      ? { month: search.month }
      : {}),
    ...(typeof search.company === "string" && /^[0-9a-f-]{36}$/i.test(search.company)
      ? { company: search.company }
      : {}),
  }),
  component: function OperatorBillingScreen() {
    const { api } = operatorBillingRoute.useRouteContext();
    const { tab, month, company } = operatorBillingRoute.useSearch();
    const navigate = useNavigate();
    const { me } = useSession();
    if (!isOperator(me))
      return (
        <PlainShell me={me} api={api} current={null}>
          <NotFoundPage />
        </PlainShell>
      );
    const current = utcMonth(new Date());
    return (
      <OperatorShell me={me} api={api}>
        <OperatorBillingPage
          api={api}
          tab={tab ?? "prices"}
          month={month ?? current}
          company={company}
          onChange={(next) =>
            void navigate({
              to: "/operator/billing",
              search: {
                ...(next.tab !== "prices" ? { tab: next.tab } : {}),
                ...(next.month !== current ? { month: next.month } : {}),
              },
            })
          }
        />
      </OperatorShell>
    );
  },
});
